import logger from '../utils/logger';
import { AxiosError } from 'axios';
import { CustomError } from '../CustomError';
import { ErrorCodes } from '../constants/credit';

/**
 * Centralized error handler for API requests
 * @param error The caught error
 * @param context Description of where the error occurred (for logging)
 * @throws CustomError with appropriate error code
 */
export const handleApiError = (error: unknown, context: string): never => {
  const axiosError = error as AxiosError;

  // Network errors
  if (axiosError.code === 'ERR_NETWORK') {
    logger('error', `Network error at ${context}`, axiosError);
    throw new CustomError('Network error', ErrorCodes.INVALID_REQUEST);
  }

  // Handle different HTTP status codes
  if (axiosError.response) {
    const { status } = axiosError.response;

    // Extract error message safely
    let errorMessage = '';
    try {
      const responseData = axiosError.response.data;
      if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData && typeof responseData === 'object') {
        const typedData = responseData as Record<string, unknown>;
        errorMessage = typeof typedData.message === 'string' ? typedData.message : '';
      }
    } catch (e) {
      errorMessage = '';
    }

    // Special case for credit application not found
    if (status === 404 && errorMessage.includes('No credit application found for the company')) {
      return null as never;
    }

    // Special case for reCAPTCHA validation failure
    if (
      status === 500 &&
      (errorMessage.toLowerCase().includes('invalid recaptcha') ||
        errorMessage.toLowerCase().includes('recaptcha verification failed'))
    ) {
      logger('error', `reCAPTCHA validation failed at ${context}`, axiosError);
      throw new CustomError('reCAPTCHA validation failed', ErrorCodes.VERIFICATION_FAILED);
    }

    // Special case for already submitted credit application
    if (
      status === 400 &&
      errorMessage.includes('Credit application already submitted for this company')
    ) {
      logger('info', `Credit application already submitted at ${context}`, axiosError);
      throw new CustomError(
        'Credit application already submitted',
        ErrorCodes.FORM_ALREADY_SUBMITTED,
      );
    } else if (
      status === 400 &&
      errorMessage.includes('Credit limit for given company is already set.')
    ) {
      logger(
        'info',
        `Credit application already submitted or credit limit set at ${context}`,
        axiosError,
      );
      throw new CustomError('Credit limit already set', ErrorCodes.CREDIT_LIMIT_ALREADY_SET);
    }

    // Handle specific status codes
    switch (status) {
      case 400:
        logger('error', `Bad request at ${context}`, axiosError);
        throw new CustomError('Bad request', ErrorCodes.UNEXPECTED_ERROR);
      case 401:
        // Check if the error message indicates token expiration
        if (
          errorMessage.toLowerCase().includes('expired') ||
          errorMessage.toLowerCase().includes('token') ||
          errorMessage.toLowerCase().includes('jwt')
        ) {
          logger('error', `Session expired at ${context}`, axiosError);
          throw new CustomError('Session expired', ErrorCodes.SESSION_EXPIRED);
        } else {
          logger('error', `Authentication failed at ${context}`, axiosError);
          throw new CustomError('Authentication failed', ErrorCodes.AUTHENTICATION_FAILED);
        }
      case 403:
        logger('error', `Access denied at ${context}`, axiosError);
        throw new CustomError('Access denied', ErrorCodes.AUTHENTICATION_FAILED);
      case 404:
        logger('error', `Resource not found at ${context}`, axiosError);
        throw new CustomError('Resource not found', ErrorCodes.INVALID_REQUEST);
      case 409:
        logger('error', `Conflict error at ${context}`, axiosError);
        throw new CustomError('Conflict error', ErrorCodes.FORM_ALREADY_SUBMITTED);
      case 429:
        logger('error', `Too many requests at ${context}`, axiosError);
        throw new CustomError('Too many requests', ErrorCodes.BLOCKED_REQUEST);
      case 500:
      case 502:
      case 503:
      case 504:
        logger('error', `Server error at ${context}`, axiosError);
        throw new CustomError('Server error', ErrorCodes.SYSTEM_FAILURE);
      default:
        logger('error', `Unexpected error at ${context}`, axiosError);
        throw new CustomError('Unexpected error', ErrorCodes.UNEXPECTED_ERROR);
    }
  }

  // Log and throw a generic error for unhandled cases
  logger('error', `Unhandled error at ${context}`, error);
  throw new CustomError('Unexpected error', ErrorCodes.UNEXPECTED_ERROR);
};
